/* eslint-disable no-empty-function */
/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { object, string } from 'yup';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { useFormik } from 'formik';

// Components
import { TextInput } from 'wg-fe-ui';

// Services
import { getOptionObject } from './services/proFlowService';
import { isObjectEmpty } from '../../../common/src/legacy/services/objectService';
import {
  retrieveAddressStreetsREST,
  retrieveAddressCitiesREST,
} from '../../../common/src/legacy/services/apiRouterService';

// Types
import { Address, City, CityResp, StreetResp } from '../../../../types/Common';
import { Option } from '../../../../types/Form';
import cogoToast from 'cogo-toast';
import BasicCountryPicker from '../../../common/src/components/BasicCountryPicker';
import SearchSelectInputV2 from '../../../common/src/components/SearchSelectInputV2';
import { ReturnOptionProps } from '../../../../types/ProductFactory';
import AddressSearchSelectorV2 from '../../../common/src/legacy/features/address-selector-v2/AddressSearchSelectorV2';
import QuestionTitle from '../../../sales-conversation/src/components/legacy/questions/QuestionTitle';
import { mq } from '../../../common/src/utils/styledComponentsUtils';

interface Props {
  title?: string;
  onSubmit: (arg0: Address) => void;
  value?: Address;
  error?: string;
  prefilled?: boolean;
  session?: boolean;
  withCountry?: boolean;
  isManualActive?: boolean;
  disabled?: boolean;
  triggerChange?: () => void;
  alwaysShowSearch?: boolean;
  updateOnChange?: boolean;
  countryCode?: string;
}

interface CityOption {
  label: string;
  value: { name: string; zipcode: string };
}

export const canFetch = (value: string): boolean =>
  value.length >= 2 && !!value.match(/[A-Za-z]{2,}/);

const ProFlowAddressComponentV2: React.FC<React.PropsWithChildren<Props>> = ({
  title,
  onSubmit,
  value,
  error: passedError,
  prefilled: prefilledProp,
  session,
  withCountry,
  isManualActive: passedIsManualActive,
  disabled,
  triggerChange,
  alwaysShowSearch = true,
  updateOnChange,
  countryCode,
  ...otherProps
}) => {
  const { t } = useTranslation();
  const [municipalityNameOptions, setMunicipalityNameOptions] =
    useState<CityOption[]>();
  const initialRender = useRef<boolean>(true);
  const [streets, setStreets] = useState<Option[]>();
  const [prefilled, setPrefilled] = useState<{ [x: string]: boolean }>({});
  const [isManualActive, setIsManualActive] = useState<boolean>(
    passedIsManualActive || false,
  );

  const addressSchema = object().shape({
    country_code: string(),
    street: string()
      .matches(
        /^([a-zA-z0-9/\\'',-\s]{2,255})$/,
        t('The answer contains invalid characters'),
      )
      .required(t('required')),
    housenr: string()
      .matches(/[aA-zZ\s0-9]+/, t('Only alphanumeric characters are allowed'))
      .matches(/^\d+/, t('Should start with a number'))
      .required(t('required')),
    boxnr: string()
      .matches(/^[aA-zZ\s0-9]+$/, t('Only alphanumeric characters are allowed'))
      .nullable(),
    zipcode: string()
      .matches(/^[aA-zZ\s0-9]+$/, t('Only alphanumeric characters are allowed'))
      .required(t('required')),
    city: string()
      .matches(
        /^([a-zA-z0-9/\\'',-\s]{2,255})$/,
        t('The answer contains invalid characters'),
      )
      .required(t('required')),
  });

  const { values, errors, setFieldValue, setValues, resetForm } = useFormik({
    validationSchema: addressSchema,
    initialValues: {
      ...value,
    },
    onSubmit: () => {},
  });

  useEffect(() => {
    if (updateOnChange) {
      if (value) {
        setValues(value);
      } else {
        resetForm();
      }
    }
  }, [value]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else if (values && !isObjectEmpty(values)) {
      onSubmit(values);
    }
  }, [values]);

  useEffect(() => {
    if (value) {
      setPrefilled(
        Object.assign(
          {},
          ...Object.keys(value).map((key) => ({
            [key]: values[key] && prefilledProp,
          })),
        ),
      );
    }
  }, [prefilledProp]);

  useEffect(() => {
    if (passedIsManualActive != null) setIsManualActive(passedIsManualActive);
  }, [passedIsManualActive]);

  const fetchCityNames = async (
    value: string,
    cb: (arg0?: CityOption[]) => void,
  ) => {
    // BE gives error when < 2 characters are send
    if (!canFetch(value)) return cb([]);

    const [resp, status] = await retrieveAddressCitiesREST({
      searchQuery: value,
      zipcode: null,
      countryCode: countryCode,
    });
    if (status !== 200) return cogoToast.error('error');

    const cities = (resp as CityResp).items.map(({ name, zipcode }) => ({
      value: { name: name || '', zipcode: zipcode?.toString() || '' },
      label: name || '',
    }));

    setMunicipalityNameOptions(cities);
    cb(cities);
  };

  const fetchStreets = async (
    value: string,
    cb: (arg0: Option[] | never[]) => void,
  ) => {
    // BE gives error when < 2 characters are send
    if (!canFetch(value)) return cb([]);

    const [resp, status] = await retrieveAddressStreetsREST({
      searchQuery: value,
      city: values?.city,
      countryCode: countryCode,
    });
    if (status !== 200) return;

    const streets = (resp as StreetResp).items.map(({ name }) => ({
      value: name,
      label: name,
    }));

    setStreets(streets);
    cb(streets);
  };

  const [debounceFetchCityNames] = useDebouncedCallback(fetchCityNames, 500);
  const [debounceFetchStreets] = useDebouncedCallback(fetchStreets, 500);

  // Event handlers
  const onChange = ({ name, value }: ReturnOptionProps) => {
    triggerChange && triggerChange();
    setPrefilled((prefilled) => ({ ...prefilled, [name]: false }));
    setFieldValue(name, value);

    // If anything changes reset the lng and lat
    if (values.lng || values.lat) {
      setFieldValue('lng', null);
      setFieldValue('lat', null);
    }
  };

  const handleOnMunicipalityNameSelected = ({
    name,
    value: passedValue,
  }: ReturnOptionProps) => {
    resetForm({
      values: {
        ...value,
        city: '',
        street: '',
        housenr: '',
        boxnr: null,
        lng: null,
        lat: null,
      },
    });
    onChange({ name, value: (passedValue as City).name, label: name });
    onChange({
      name: 'zipcode',
      value: (passedValue as City).zipcode,
      label: name,
    });
    setPrefilled((prefilled) =>
      Object.assign(
        {},
        ...Object.keys(prefilled).map((key) => ({ [key]: false })),
      ),
    );
  };

  const getMunicipalityOptionObject = (
    selectedOptionValue: string | Option,
  ) => {
    if (typeof selectedOptionValue === 'object') return selectedOptionValue;
    if (!municipalityNameOptions)
      return {
        value: selectedOptionValue,
        label: selectedOptionValue,
      };

    return (
      municipalityNameOptions.find(
        ({ value }) => value.name === selectedOptionValue,
      ) || {
        value: '',
        label: '',
      }
    );
  };

  const showAddressSearch =
    (!values || isObjectEmpty(values)) && !isManualActive;

  return (
    <Wrapper {...otherProps}>
      {(alwaysShowSearch || showAddressSearch) && (
        <AddressSearchSelectorV2
          error={(showAddressSearch && passedError) || ''}
          onSelected={(address: ReturnOptionProps) =>
            setValues(address.value ? (address.value as Address) : {})
          }
          setIsManualActive={setIsManualActive}
          countryCode={countryCode}
        >
          {title}
        </AddressSearchSelectorV2>
      )}
      {alwaysShowSearch && !showAddressSearch && <Line />}
      {(!alwaysShowSearch || !showAddressSearch) && (
        <AddressGrid>
          {countryCode === 'BE' ? (
            <StyledSearchSelectInput
              async
              cacheOptions={false}
              name="city"
              value={getMunicipalityOptionObject(values?.city || '')}
              error={errors.city as string}
              loadOptions={debounceFetchCityNames}
              onSelected={(value: ReturnOptionProps) =>
                handleOnMunicipalityNameSelected(value)
              }
              disabled={disabled}
              prefilled={prefilled.city}
            >
              <StyledQuestionTitle>{t('Municipality')} *</StyledQuestionTitle>
            </StyledSearchSelectInput>
          ) : (
            <StyledTextInput
              name="city"
              value={values?.city}
              error={errors.city}
              onChange={onChange}
              prefilled={prefilled.city}
              placeholder={t('Municipality')}
              disabled={disabled}
            >
              <StyledQuestionTitle>{t('Municipality')} *</StyledQuestionTitle>
            </StyledTextInput>
          )}
          <StyledTextInput
            name="zipcode"
            value={values?.zipcode}
            error={errors.zipcode}
            onChange={onChange}
            prefilled={prefilled.zipcode}
            placeholder={t('zipcode')}
            withCountry={true}
            disabled={disabled}
          >
            <StyledQuestionTitle>{t('Postal code')} *</StyledQuestionTitle>
          </StyledTextInput>
          {countryCode === 'BE' ? (
            <StyledSearchSelectInput
              async
              cacheOptions={false}
              name="street"
              value={getOptionObject(streets, values?.street)}
              error={errors.street as string}
              loadOptions={debounceFetchStreets}
              onSelected={onChange}
              withCountry={withCountry}
              disabled={disabled}
              prefilled={prefilled.street}
              grow
            >
              <StyledQuestionTitle>{t('Street')} *</StyledQuestionTitle>
            </StyledSearchSelectInput>
          ) : (
            <StyledTextInput
              name="street"
              value={values?.street}
              error={errors.street}
              onChange={onChange}
              prefilled={prefilled.street}
              placeholder={t('Street')}
              disabled={disabled}
            >
              <StyledQuestionTitle>{t('Street')} *</StyledQuestionTitle>
            </StyledTextInput>
          )}
          <SubGrid withCountry={withCountry}>
            <StyledTextInput
              name="housenr"
              value={values?.housenr}
              error={errors.housenr}
              onChange={onChange}
              prefilled={prefilled.housenr}
              disabled={!values?.street || disabled}
              maxLength="10"
              withCountry={withCountry}
              span={countryCode === 'NL' ? 2 : 1}
            >
              <StyledQuestionTitle>{t('Housenr')} *</StyledQuestionTitle>
            </StyledTextInput>
            {countryCode === 'BE' && (
              <StyledTextInput
                name="boxnr"
                value={values?.boxnr}
                error={errors.boxnr}
                onChange={onChange}
                prefilled={prefilled.boxnr}
                disabled={!values?.street || disabled}
                withCountry={withCountry}
              >
                <StyledQuestionTitle>{t('Boxnr')}</StyledQuestionTitle>
              </StyledTextInput>
            )}
            {withCountry && (
              <StyledBasicCountryPicker
                name="country_code"
                onSelected={onChange}
                prefilled={prefilled.country_code}
                error={errors.country_code}
                value={values.country_code}
                disabled={disabled}
                withDefault
              />
            )}
          </SubGrid>
        </AddressGrid>
      )}
    </Wrapper>
  );
};

const StyledQuestionTitle = styled(QuestionTitle)`
  color: black;
  font-size: 1.6rem;
  margin-bottom: 0;
`;

const Line = styled.div`
  margin: 3.2rem 0;
  border: 1px solid #cccccc;
`;

const Wrapper = styled.div`
  width: 100%;
`;

const StyledSearchSelectInput = styled(SearchSelectInputV2)<{
  grow?: boolean;
  withCountry?: boolean;
  prefilled?: boolean;
}>`
  height: 100%;

  label > div {
    border-radius: 6px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
      0 0 1px rgba(0, 0, 0, 0.04);
    margin-bottom: 0;
    margin-top: 0;
  }

  & .Select__control {
    ${({ prefilled, error }) =>
      prefilled ? `border-color: #FFBC0F; background-color: #FFF9EA;` : ''}
  }

  @media screen and (max-width: 1200px) {
    grid-column: span
      ${({ grow, withCountry }) => (grow && withCountry ? 2 : 1)};
  }

  @media screen and (max-width: 760px) {
    grid-column: span 2;
  }
`;

const StyledTextInput = styled(TextInput)<{
  span?: number;
  withCountry?: boolean;
  isNL?: boolean;
}>`
  white-space: nowrap;
  height: 100%;
  > label > input {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
      0 0 1px rgba(0, 0, 0, 0.04);
    border: 1px solid #e4e4e4;
    border-radius: 4px;
    padding-left: 1.2rem;
    height: 4.5rem;
  }

  > div {
    margin-bottom: 0;
  }

  & input {
    ${({ prefilled, error }) =>
      prefilled ? `border-color: #FFBC0F; background-color: #FFF9EA;` : ''}
  }
  grid-column: span ${({ span }) => (span ? span : 1)};

  @media screen and (max-width: 760px) {
    grid-column: span
      ${({ withCountry, isNL }) => (withCountry || isNL ? 2 : 1)};
  }
`;

const StyledBasicCountryPicker = styled(BasicCountryPicker)`
  grid-column: span 2;

  & .Select__control {
    ${({ prefilled, error }) =>
      prefilled ? `border-color: #FFBC0F; background-color: #FFF9EA;` : ''}
  }

  label {
    line-height: 110%;
    height: 1.5rem;
  }

  > label > div {
    margin-top: 1.6rem;
  }
`;

const SubGrid = styled.div<{ withCountry?: boolean }>`
  display: grid;
  grid-template-columns: repeat(
    ${({ withCountry }) => (withCountry ? 4 : 2)},
    1fr
  );
  grid-column-gap: 1rem;

  ${({ withCountry }) =>
    withCountry
      ? css`
          @media screen and (max-width: 1200px) {
            grid-column: span 2;
          }
        `
      : css`
          @media screen and (max-width: 760px) {
            grid-column: span 2;
          }
        `}
`;

const AddressGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 2.4rem;

  ${mq.laptop} {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 2.4rem;
  }
`;

export default ProFlowAddressComponentV2;
